import { SlotImg, ReplaceSlot, useSlot } from "@/components/site/AssetImage";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ExternalLink, Check } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { PAYPAL_URL, PHONE } from "@/lib/assets";

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const t = document.createElement("textarea");
      t.value = value;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" onClick={copy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-[#24453D] px-4 text-sm font-semibold hover:bg-[#24453D] hover:text-white"
      data-testid={testId} aria-live="polite">
      {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Method({ name, value, href, children, testId }) {
  return (
    <div className="rounded-3xl border border-[#E5DDD3] bg-white p-5" data-testid={testId}>
      <p className="font-display text-lg font-semibold">{name}</p>
      {value ? (
        <div className="mt-2 space-y-3">
          <p className="break-all font-mono text-[15px]" data-testid={`${testId}-handle`}>{value}</p>
          <div className="flex flex-wrap gap-2">
            <CopyButton value={value} testId={`${testId}-copy-button`} />
            {href && (
              <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 text-sm font-semibold text-[var(--pg-ink)] underline underline-offset-4"
                data-testid={`${testId}-link`}>Open in {name}<ExternalLink className="h-4 w-4" aria-hidden /><span className="sr-only"> (opens in a new tab)</span></a>
            )}
          </div>
          {children}
        </div>
      ) : (
        <p className="mt-2 text-[#3B4A44]" data-testid={`${testId}-coming-soon`}>Coming soon. Text or call {PHONE}.</p>
      )}
    </div>
  );
}

export function OtherWays({ level: Heading = "h3" }) {
  const qr = useSlot("PAYPAL_QR");
  const { settings } = useSite();
  const s = settings || {};
  return (
    <div className="mt-8" data-testid="other-ways-section">
      <Heading className="font-display text-2xl font-semibold">Other ways to give</Heading>
      <div className="mt-5 grid gap-5 lg:grid-cols-[1.2fr_1fr]">
        <div className="of-card flex flex-col items-center gap-6 p-6 sm:flex-row sm:items-center" data-testid="paypal-card">
          <figure className="flex flex-col items-center">
            <div className="relative rounded-2xl bg-white p-3 ring-1 ring-[#E5DDD3]">
              {qr.up ? (
                <SlotImg up={qr.up} alt={qr.alt} fit="contain" sizes="160px" className="h-40 w-40" testId="paypal-qr-image" />
              ) : (
                <QRCodeSVG value={PAYPAL_URL} size={160} level="M" title="PayPal QR code" data-testid="paypal-qr-code" />
              )}
              <ReplaceSlot asset="PAYPAL_QR" className="!-bottom-4" />
            </div>
            <figcaption className="mt-2 text-sm font-semibold" data-testid="paypal-qr-label">Scan to give with PayPal</figcaption>
          </figure>
          <div className="text-center sm:text-left">
            <p className="font-display text-xl font-semibold">PayPal</p>
            <p className="mt-1 text-[#3B4A44]">Prefer PayPal? Tap the button or scan the code with your phone.</p>
            <a href={PAYPAL_URL} target="_blank" rel="noopener noreferrer" className="of-btn-donate mt-4" data-testid="paypal-button">
              Give with PayPal <ExternalLink className="h-4 w-4" aria-hidden /><span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        </div>
        <div className="grid gap-4">
          <Method name="Venmo" value={s.venmo ? `@${s.venmo}` : ""} href={s.venmo ? `https://venmo.com/u/${encodeURIComponent(s.venmo)}` : ""} testId="venmo-method" />
          <Method name="Zelle" value={s.zelle} testId="zelle-method" />
          <Method name="Bitcoin" value={s.bitcoin} testId="bitcoin-method">
            {s.bitcoin && (
              <div className="inline-block rounded-2xl bg-white p-2 ring-1 ring-[#E5DDD3]">
                <QRCodeSVG value={`bitcoin:${s.bitcoin}`} size={120} title="Bitcoin address QR code" data-testid="bitcoin-qr-code" />
              </div>
            )}
          </Method>
        </div>
      </div>
    </div>
  );
}
